import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Collapse,
    Paper,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    InputLabel,
    Tooltip,
    LinearProgress
} from '@mui/material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

// icons
import {
    IconSearch,
    IconChevronDown,
    IconChevronUp,
    IconRefresh,
    IconFileExport,
    IconCircleCheck,
    IconAlertCircle,
    IconClock,
    IconEye,
    IconCopy
} from '@tabler/icons-react';

// Generate mock execution history
const generateMockExecutions = () => {
    const executions = [];
    const functionNames = ['calculate_discount', 'process_payment', 'analyze_sentiment', 'generate_report', 'validate_data'];
    const statuses = ['success', 'success', 'success', 'success', 'failed'];

    for (let i = 0; i < 50; i++) {
        const funcName = functionNames[Math.floor(Math.random() * functionNames.length)];
        const status = statuses[Math.floor(Math.random() * statuses.length)];
        const executionTime = Math.floor(Math.random() * 200) + 10;

        executions.push({
            id: `exec_${Date.now()}_${i}`,
            request_id: `req_${Math.random().toString(36).substr(2, 9)}`,
            function_name: funcName,
            function_id: `fn_tenant_123_${funcName}_${Math.random().toString(36).substr(2, 8)}`,
            status,
            execution_time_ms: executionTime,
            billed_duration_ms: Math.ceil(executionTime / 100) * 100,
            memory_used_mb: Math.floor(Math.random() * 256) + 32,
            cold_start: Math.random() > 0.8,
            input_data: generateMockInput(funcName),
            output_data: status === 'success' ? generateMockOutput(funcName) : null,
            error: status === 'failed' ? generateMockError() : null,
            created_at: new Date(Date.now() - Math.random() * 86400000).toISOString(),
            user_id: `user_${Math.floor(Math.random() * 100)}`,
            ip_address: `192.168.1.${Math.floor(Math.random() * 255)}`,
            api_key_id: `key_${Math.random().toString(36).substr(2, 8)}`
        });
    }

    return executions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
};

const generateMockInput = (funcName) => {
    const inputs = {
        calculate_discount: { price: 100, quantity: 5, customer_tier: 'gold' },
        process_payment: { amount: 99.99, currency: 'USD', payment_method: { type: 'credit_card' } },
        analyze_sentiment: { text: 'Great product!', language: 'en' },
        generate_report: { report_type: 'sales', data: { start_date: '2024-07-01' } },
        validate_data: { data: { name: 'John', age: 30 }, schema: { name: 'string' } }
    };
    return inputs[funcName] || {};
};

const generateMockOutput = (funcName) => {
    const outputs = {
        calculate_discount: { discount: 15, final_price: 425 },
        process_payment: { transaction_id: 'txn_123', status: 'completed' },
        analyze_sentiment: { sentiment: 'positive', confidence: 0.92 },
        generate_report: { report_url: 'https://example.com/report.pdf' },
        validate_data: { valid: true, errors: [] }
    };
    return outputs[funcName] || { status: 'success' };
};

const generateMockError = () => ({
    type: 'ValidationError',
    message: 'Invalid input parameter',
    stack_trace: 'File "/handler.py", line 15, in lambda_handler'
});

// ==============================|| EXECUTION HISTORY ||============================== //

const ExecutionHistory = () => {
    const theme = useTheme();
    const { functions } = useSelector((state) => state.lambdaFunctions);

    const [executions, setExecutions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expandedRows, setExpandedRows] = useState({});
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        functionId: 'all',
        timeRange: '24h'
    });

    useEffect(() => {
        // Simulate loading executions
        setLoading(true);
        setTimeout(() => {
            setExecutions(generateMockExecutions());
            setLoading(false);
        }, 1000);
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleExpandRow = (executionId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [executionId]: !prev[executionId]
        }));
    };

    const handleRefresh = () => {
        setLoading(true);
        setTimeout(() => {
            setExecutions(generateMockExecutions());
            setLoading(false);
        }, 1000);
    };

    const handleExport = () => {
        const data = filteredExecutions.map((exec) => ({
            request_id: exec.request_id,
            function_name: exec.function_name,
            status: exec.status,
            execution_time_ms: exec.execution_time_ms,
            created_at: exec.created_at
        }));

        const csv = [Object.keys(data[0]).join(','), ...data.map((row) => Object.values(row).join(','))].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `executions_${new Date().toISOString()}.csv`;
        a.click();
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    };

    // Filter executions
    const filteredExecutions = executions.filter((exec) => {
        const matchesSearch = exec.request_id.includes(filters.search) || exec.function_name.includes(filters.search);
        const matchesStatus = filters.status === 'all' || exec.status === filters.status;
        const matchesFunction = filters.functionId === 'all' || exec.function_id === filters.functionId;

        // Time range filter
        const execTime = new Date(exec.created_at);
        const now = new Date();
        let matchesTime = true;

        if (filters.timeRange === '1h') {
            matchesTime = now - execTime < 3600000;
        } else if (filters.timeRange === '24h') {
            matchesTime = now - execTime < 86400000;
        } else if (filters.timeRange === '7d') {
            matchesTime = now - execTime < 604800000;
        }

        return matchesSearch && matchesStatus && matchesFunction && matchesTime;
    });

    const paginatedExecutions = filteredExecutions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h4">Execution History</Typography>

                    <Box display="flex" gap={1}>
                        <Tooltip title="Export CSV">
                            <IconButton onClick={handleExport}>
                                <IconFileExport size={20} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh">
                            <IconButton onClick={handleRefresh} disabled={loading}>
                                <IconRefresh size={20} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Filters */}
                <Box display="flex" gap={2} mb={3} alignItems="center">
                    <TextField
                        placeholder="Search by request ID or function..."
                        variant="outlined"
                        size="small"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        sx={{ flexGrow: 1, maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconSearch size={18} />
                                </InputAdornment>
                            )
                        }}
                    />

                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Status</InputLabel>
                        <Select value={filters.status} label="Status" onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                            <MenuItem value="all">All Status</MenuItem>
                            <MenuItem value="success">Success</MenuItem>
                            <MenuItem value="failed">Failed</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Function</InputLabel>
                        <Select
                            value={filters.functionId}
                            label="Function"
                            onChange={(e) => setFilters({ ...filters, functionId: e.target.value })}
                        >
                            <MenuItem value="all">All Functions</MenuItem>
                            {functions.map((func) => (
                                <MenuItem key={func.id} value={func.id}>
                                    {func.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={filters.timeRange}
                            label="Time Range"
                            onChange={(e) => setFilters({ ...filters, timeRange: e.target.value })}
                        >
                            <MenuItem value="1h">Last Hour</MenuItem>
                            <MenuItem value="24h">Last 24 Hours</MenuItem>
                            <MenuItem value="7d">Last 7 Days</MenuItem>
                            <MenuItem value="all">All Time</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {loading && <LinearProgress sx={{ mb: 2 }} />}

                {/* Table */}
                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>Request ID</TableCell>
                                <TableCell>Function</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Memory</TableCell>
                                <TableCell>Time</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedExecutions.map((execution) => (
                                <React.Fragment key={execution.id}>
                                    <TableRow
                                        hover
                                        sx={{
                                            '& > *': { borderBottom: 'unset' },
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <TableCell>
                                            <IconButton size="small" onClick={() => handleExpandRow(execution.id)}>
                                                {expandedRows[execution.id] ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                {execution.request_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">{execution.function_name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    v1.0.0
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={execution.status}
                                                size="small"
                                                color={execution.status === 'success' ? 'success' : 'error'}
                                                icon={
                                                    execution.status === 'success' ? (
                                                        <IconCircleCheck size={14} />
                                                    ) : (
                                                        <IconAlertCircle size={14} />
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={0.5}>
                                                <IconClock size={14} />
                                                <Typography variant="body2">{execution.execution_time_ms}ms</Typography>
                                                {execution.cold_start && (
                                                    <Chip label="Cold Start" size="small" variant="outlined" color="warning" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{execution.memory_used_mb}MB</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{new Date(execution.created_at).toLocaleString()}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box display="flex" gap={0.5} justifyContent="flex-end">
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => handleExpandRow(execution.id)}>
                                                        <IconEye size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Copy Request ID">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigator.clipboard.writeText(execution.request_id)}
                                                    >
                                                        <IconCopy size={16} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                                            <Collapse in={expandedRows[execution.id]} timeout="auto" unmountOnExit>
                                                <Box sx={{ margin: 2 }}>
                                                    <Typography variant="h6" gutterBottom component="div">
                                                        Execution Details
                                                    </Typography>

                                                    <Box display="flex" gap={2} mb={2}>
                                                        <Chip label={`User: ${execution.user_id}`} size="small" variant="outlined" />
                                                        <Chip label={`IP: ${execution.ip_address}`} size="small" variant="outlined" />
                                                        <Chip label={`API Key: ${execution.api_key_id}`} size="small" variant="outlined" />
                                                        <Chip
                                                            label={`Billed: ${execution.billed_duration_ms}ms`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </Box>

                                                    <Box display="flex" gap={2}>
                                                        <Box flex={1}>
                                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                                <Typography variant="subtitle2">Input</Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => copyToClipboard(execution.input_data)}
                                                                >
                                                                    <IconCopy size={14} />
                                                                </IconButton>
                                                            </Box>
                                                            <SyntaxHighlighter
                                                                language="json"
                                                                style={vscDarkPlus}
                                                                customStyle={{
                                                                    margin: 0,
                                                                    borderRadius: 4,
                                                                    fontSize: '0.875rem',
                                                                    maxHeight: 200,
                                                                    overflow: 'auto'
                                                                }}
                                                            >
                                                                {JSON.stringify(execution.input_data, null, 2)}
                                                            </SyntaxHighlighter>
                                                        </Box>

                                                        <Box flex={1}>
                                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                                <Typography variant="subtitle2">
                                                                    {execution.status === 'success' ? 'Output' : 'Error'}
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        copyToClipboard(execution.output_data || execution.error)
                                                                    }
                                                                >
                                                                    <IconCopy size={14} />
                                                                </IconButton>
                                                            </Box>
                                                            <SyntaxHighlighter
                                                                language="json"
                                                                style={vscDarkPlus}
                                                                customStyle={{
                                                                    margin: 0,
                                                                    borderRadius: 4,
                                                                    fontSize: '0.875rem',
                                                                    maxHeight: 200,
                                                                    overflow: 'auto'
                                                                }}
                                                            >
                                                                {JSON.stringify(execution.output_data || execution.error, null, 2)}
                                                            </SyntaxHighlighter>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredExecutions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </CardContent>
        </Card>
    );
};

export default ExecutionHistory;
